import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Gauge, MapPin, RefreshCw, ExternalLink } from "lucide-react";

/**
 * Live Norwegian road fact widget — reads /api/nvdb/road-fact and shows
 * a single posted speed limit from the National Road Database (NVDB).
 * Data: Statens vegvesen, NLOD-licensed.
 */
export default function NvdbRoadFactWidget() {
  const { t } = useLang();
  const [fact, setFact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get("/nvdb/road-fact");
      setFact(r.data);
    } catch (e) {
      console.error("NVDB road-fact fetch failed:", e);
      setError(true);
      setFact(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card
      data-testid="nvdb-road-fact-widget"
      className="p-6 bg-white border border-gray-200 relative overflow-hidden"
    >
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <Badge variant="outline" className="mb-2 text-[10px] uppercase tracking-wide">
            {t("liveData")}
          </Badge>
          <h3 className="font-heading text-lg font-semibold text-gray-900">
            {t("nvdbWidgetTitle")}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">{t("nvdbWidgetSubtitle")}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="nvdb-refresh-btn"
          aria-label={t("refresh")}
          className="shrink-0"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading && !fact && (
        <div data-testid="nvdb-loading" className="py-8 text-center text-sm text-gray-500">
          {t("loading")}
        </div>
      )}

      {error && (
        <div data-testid="nvdb-error" className="py-6 text-center">
          <p className="text-sm text-gray-600 mb-3">{t("nvdbError")}</p>
          <Button size="sm" variant="outline" onClick={load} data-testid="nvdb-retry-btn">
            {t("tryAgain")}
          </Button>
        </div>
      )}

      {fact && !error && (
        <div data-testid="nvdb-fact" className="space-y-4">
          <div className="flex items-baseline gap-3">
            <div
              className="bg-fjord text-white rounded-full w-20 h-20 grid place-items-center font-heading font-bold text-3xl shadow-sm shrink-0"
              data-testid="nvdb-speed-kmh"
              aria-label={`${fact.speed_limit_kmh} km/h`}
            >
              {fact.speed_limit_kmh}
            </div>
            <div className="min-w-0">
              <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <Gauge className="w-3.5 h-3.5" />
                {t("postedSpeedLimit")}
              </div>
              <div className="font-heading text-xl font-semibold text-gray-900 leading-tight">
                {fact.speed_limit_kmh} km/h
              </div>
              {fact.valid_from && (
                <div className="text-[11px] text-gray-500 mt-1">
                  {t("validFrom")}: {fact.valid_from}
                </div>
              )}
            </div>
          </div>

          {fact.road_reference && (
            <div className="flex items-start gap-2 text-sm text-gray-700 bg-gray-50 rounded-md p-3 border border-gray-100">
              <MapPin className="w-4 h-4 text-fjord shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-0.5">
                  {t("roadReference")}
                </div>
                <div className="font-mono text-xs sm:text-sm break-all" data-testid="nvdb-road-ref">
                  {fact.road_reference}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-1 text-[11px] text-gray-500">
            <span>{fact.source}</span>
            {fact.docs && (
              <a
                href={fact.docs}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-fjord hover:underline"
                data-testid="nvdb-docs-link"
              >
                NVDB <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
